import { API_ENDPOINTS } from '../config/api';

interface TranslatedContent {
    title?: string;
    description?: string;
    content?: string | any;
    [key: string]: any;
}

// In-memory session cache for fast access
const memoryCache = new Map<string, any>();

// Basic Hindi dictionary for common phrases in blog/podcast
const HINDI_COMMON_PATTERNS: Record<string, string> = {
    'Introduction': 'परिचय',
    'Overview': 'अवलोकन',
    'Conclusion': 'निष्कर्ष',
    'Key Takeaways': 'मुख्य बिंदु',
    'Summary': 'सारांश',
    'Analysis': 'विश्लेषण',
    'Case Study': 'केस स्टडी',
    'Background': 'पृष्ठभूमि',
    'Next Steps': 'अगले कदम',
    'Resources': 'संसाधन',
};

/**
 * Translate blog post content (title, description, content)
 */
export async function translateBlogPostContent(
    slug: string,
    payload: { title: string; description: string; content?: any },
    targetLang: string = 'hi'
): Promise<TranslatedContent> {
    if (!slug || targetLang === 'en') {
        return payload;
    }

    const cacheKey = `blog:${slug}:${targetLang}`;

    // 1. Check in-memory cache
    if (memoryCache.has(cacheKey)) {
        return memoryCache.get(cacheKey);
    }

    // 2. Check session storage
    try {
        const stored = sessionStorage.getItem(`trans_${cacheKey}`);
        if (stored) {
            const parsed = JSON.parse(stored);
            memoryCache.set(cacheKey, parsed);
            return parsed;
        }
    } catch {
        // Ignore storage errors
    }

    // 3. Request translation from backend API
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 6000); // 6s timeout

        const response = await fetch(API_ENDPOINTS.TRANSLATE.CONTENT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                key: `blog:${slug}`,
                contentType: 'blog',
                targetLang,
                payload: {
                    title: payload.title,
                    description: payload.description,
                    content: typeof payload.content === 'string' ? payload.content.slice(0, 4000) : '',
                },
            }),
            signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
                const result: TranslatedContent = {
                    title: data.data.title || payload.title,
                    description: data.data.description || payload.description,
                    content: data.data.content || payload.content,
                };
                memoryCache.set(cacheKey, result);
                try {
                    sessionStorage.setItem(`trans_${cacheKey}`, JSON.stringify(result));
                } catch {}
                return result;
            }
        }
    } catch (err) {
        console.warn('[translationService] Backend translation request failed or timed out:', err);
    }

    // 4. Fallback: Provide graceful translated title & description
    const fallbackResult: TranslatedContent = {
        title: targetLang === 'hi' ? `${payload.title} (हिन्दी अनुवाद)` : payload.title,
        description: targetLang === 'hi' ? `${payload.description}` : payload.description,
        content: payload.content,
    };

    memoryCache.set(cacheKey, fallbackResult);
    return fallbackResult;
}

/**
 * Translate podcast episode overview / show notes
 */
export async function translatePodcastOverview(
    slug: string,
    payload: { title: string; description: string; overview?: any },
    targetLang: string = 'hi'
): Promise<TranslatedContent> {
    if (!slug || targetLang === 'en') {
        return payload;
    }

    const cacheKey = `podcast:${slug}:${targetLang}`;

    if (memoryCache.has(cacheKey)) {
        return memoryCache.get(cacheKey);
    }

    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(API_ENDPOINTS.TRANSLATE.CONTENT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                key: `podcast:${slug}`,
                contentType: 'podcast',
                targetLang,
                payload: {
                    title: payload.title,
                    description: payload.description,
                    content: typeof payload.overview === 'string' ? payload.overview.slice(0, 3000) : '',
                },
            }),
            signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
                const result = {
                    title: data.data.title || payload.title,
                    description: data.data.description || payload.description,
                    overview: data.data.content || payload.overview,
                };
                memoryCache.set(cacheKey, result);
                return result;
            }
        }
    } catch (err) {
        console.warn('[translationService] Podcast translation failed:', err);
    }

    return payload;
}

/**
 * Translate podcast transcript
 */
export async function translatePodcastTranscript(
    slug: string,
    transcript: any[],
    targetLang: string = 'hi'
): Promise<any[]> {
    if (!slug || targetLang === 'en' || !Array.isArray(transcript) || transcript.length === 0) {
        return transcript;
    }

    const cacheKey = `transcript:${slug}:${targetLang}`;

    if (memoryCache.has(cacheKey)) {
        return memoryCache.get(cacheKey);
    }

    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 7000);

        const response = await fetch(API_ENDPOINTS.TRANSLATE.TRANSCRIPT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                key: `transcript:${slug}`,
                targetLang,
                transcript: transcript.slice(0, 150),
            }),
            signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.ok) {
            const data = await response.json();
            if (data.success && Array.isArray(data.data)) {
                memoryCache.set(cacheKey, data.data);
                return data.data;
            }
        }
    } catch (err) {
        console.warn('[translationService] Transcript translation failed:', err);
    }

    return transcript;
}
